import axios from 'axios';
import { tlLogger } from '@taleemabad/shared';
import { apiService, extractSyncResponse } from '../utils/request';
import { digitalCoachChatDB } from '../models/digital-coach-chat';
import { SyncRecordStatus } from '../types/sync-state';
import {
  IDcChatSession,
  IDcChatSyncResponse,
} from '../types/digital-coach-chat';

const DC_CHAT_BASE = '/api/v1/internal/digital_coach';

// Single user-facing fallback when no sanitized backend/DC error message exists.
const DC_ERROR_FALLBACK = 'Something went wrong. Please try again.';

function readDcError(err: unknown): string {
  const anyErr = err as { response?: { data?: { error?: string } } };
  return anyErr?.response?.data?.error ?? DC_ERROR_FALLBACK;
}

/**
 * Resolve the RequestTracker task_id for an observation.
 * chat/start needs the task_id, but the UI only has the observation id. The
 * existing status endpoint maps observation_id → the latest task. Returns null
 * if no processing task exists yet (button stays inert in that case).
 */
export const getTaskIdForObservation = async (
  observationId: string,
): Promise<string | null> => {
  try {
    const response = await apiService.get<{ taskId?: string }>(
      `${DC_CHAT_BASE}/status/${observationId}/`,
    );
    return response.data?.taskId ?? null;
  } catch (err) {
    tlLogger.error('getTaskIdForObservation failed', {
      observationId,
      err: err instanceof Error ? err.message : String(err),
    });
    return null;
  }
};

export const startChatSession = async (
  taskId: string,
  region = '',
): Promise<IDcChatSession & { welcomeMessage: string }> => {
  // Reuse an existing active session for this task instead of starting a fresh
  // one every click — otherwise each open spawns a new DC session and the local
  // table accumulates duplicate rows for the same task.
  const existing = await digitalCoachChatDB.getSessionByTaskId(taskId);
  if (existing && existing.status === 'active' && existing.sessionId) {
    tlLogger.log('startChatSession reusing existing session', {
      taskId,
      sessionId: existing.sessionId,
    });
    return { ...existing, welcomeMessage: '' };
  }

  try {
    const response = await apiService.post(
      `${DC_CHAT_BASE}/chat/start/${taskId}/`,
      { region },
    );
    const data = response.data as {
      sessionId: string;
      taskId?: string;
      message?: string;
    };
    const sessionId = data.sessionId;
    if (!sessionId) {
      throw new Error('Chat could not be started. Please try again.');
    }
    const nowIso = new Date().toISOString();
    const session: IDcChatSession = {
      id: sessionId,
      sessionId,
      taskId,
      region,
      status: 'active',
      created: nowIso,
      modified: nowIso,
      isActive: true,
    };
    await digitalCoachChatDB.upsertSession({
      ...session,
      syncStatus: SyncRecordStatus.Synced,
    });
    tlLogger.log('startChatSession ok', { taskId, sessionId });
    // DC returns an opening line in `message` — surface it as the first AI turn.
    return { ...session, welcomeMessage: data.message ?? '' };
  } catch (err) {
    const hasResponse = !!(err as { response?: unknown })?.response;
    const message = hasResponse
      ? readDcError(err)
      : err instanceof Error
        ? err.message
        : DC_ERROR_FALLBACK;
    tlLogger.error('startChatSession failed', { taskId, err: message });
    throw new Error(message);
  }
};

export interface IDcChatReply {
  response: string;
  language: string;
}

// DC can take a while to generate a reply; cap the wait so a hung request
// surfaces an error instead of leaving the UI in "sending" forever.
const SEND_MESSAGE_TIMEOUT_MS = 60_000;

/**
 * Send a message to the DC AI coach within an active session and return the
 * AI's reply.
 *
 * Goes DIRECT to the DC service (not through our Django backend) — our backend
 * only brokers session START. A bare `axios` call is used (not `apiService`) so
 * we don't inject our JWT / backend base URL into a cross-origin request.
 * CORS is pre-configured on the DC service: it allows this frontend origin
 * (verified via OPTIONS preflight — see `.claude/rules/dc.md`), so the
 * browser-direct POST is permitted without a backend proxy.
 *
 * Messages are NOT stored in our DB — the DC service owns the conversation; we
 * only relay the turn. Throws the sanitized error message on failure.
 */
export const sendChatMessage = async (
  sessionId: string,
  message: string,
): Promise<IDcChatReply> => {
  const rawBaseUrl = process.env.NX_PUBLIC_DC_SERVICE_URL;
  if (!rawBaseUrl) {
    tlLogger.error('sendChatMessage: NX_PUBLIC_DC_SERVICE_URL not configured', {
      sessionId,
    });
    throw new Error('Chat service is not configured.');
  }
  // Strip any trailing slash(es) so we never produce a double slash in the path.
  // The DC service contract mirrors the backend: `${base}/api/chat/<sid>/message`.
  // Non-regex loop (not /\/+$/) to avoid the S5852 super-linear-regex hotspot —
  // the input is a config env var, but a plain string trim is unambiguously safe.
  let dcBaseUrl = rawBaseUrl;
  while (dcBaseUrl.endsWith('/')) {
    dcBaseUrl = dcBaseUrl.slice(0, -1);
  }

  try {
    const url = `${dcBaseUrl}/api/chat/${sessionId}/message`;
    tlLogger.log('sendChatMessage →', { sessionId, url });
    const response = await axios.post(
      url,
      { message },
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: SEND_MESSAGE_TIMEOUT_MS,
      },
    );
    const data = response.data as { response?: string; language?: string };
    return {
      response: data.response ?? '',
      language: data.language ?? 'en',
    };
  } catch (err) {
    const hasResponse = !!(err as { response?: unknown })?.response;
    const errorMessage = hasResponse
      ? readDcError(err)
      : err instanceof Error
        ? err.message
        : DC_ERROR_FALLBACK;
    tlLogger.error('sendChatMessage failed', { sessionId, err: errorMessage });
    throw new Error(errorMessage);
  }
};

export const pullChatSessions = async (
  lastPulledAt: string | null = null,
): Promise<boolean> => {
  try {
    // ?c= is a cache-bust param — prevents CDN/reverse-proxy from serving a
    // stale session list when last_pulled_at hasn't changed between calls.
    const response = await apiService.get<IDcChatSyncResponse>(
      `${DC_CHAT_BASE}/sync-dc-chats/?c=${Date.now()}${
        lastPulledAt ? `&last_pulled_at=${lastPulledAt}` : ''
      }`,
    );
    await extractSyncResponse(
      ['dcChatSessions'],
      response.data,
      digitalCoachChatDB,
    );
    return true;
  } catch (err) {
    tlLogger.error('pullChatSessions failed', {
      err: err instanceof Error ? err.message : String(err),
    });
    return false;
  }
};

export const listChatSessions = async (): Promise<IDcChatSession[]> => {
  try {
    const response = await apiService.get<IDcChatSession[]>(
      `${DC_CHAT_BASE}/chat/sessions/`,
    );
    return response.data;
  } catch (err) {
    tlLogger.error('listChatSessions failed', {
      err: err instanceof Error ? err.message : String(err),
    });
    return [];
  }
};
